import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Field, Select } from '@/components/live';
import { callRpc } from '@/api/resources';
import { allStates } from '@/api/lookups';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';

/**
 * Add a land unit together with its hectare factor - never one without the other.
 *
 * A unit with no factor is a unit that silently reports no hectares, and hectares are what
 * EUDR and FSC reporting read. A unit whose size varies by place (Bigha, Katha, Guntha in some
 * states) must name the state its factor holds in: 1 Bigha is about 0.134 ha in West Bengal and
 * about 0.253 ha in Uttar Pradesh, and one global number would misreport one of them by nearly
 * double. Add the same unit again with another state to give it a second factor.
 *
 * Project Admin only - the database refuses anyone else.
 */
export function AreaUnitAdd({
  onAdded,
  onCancel,
}: {
  onAdded: (unitId: string) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');
  const [varies, setVaries] = useState(false);
  const [stateId, setStateId] = useState('');
  const [factor, setFactor] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const states = useQuery({
    queryKey: ['picker', 'geography', 'all-states'],
    queryFn: allStates,
    enabled: isSupabaseConfigured && varies,
  });

  async function save() {
    const value = Number(factor);
    if (!label.trim()) { toast.error('Name the unit.'); return; }
    if (!(value > 0)) { toast.error('Enter how many hectares one unit is.'); return; }
    if (varies && !stateId) { toast.error('Choose the state this factor applies in.'); return; }

    setSaving(true);
    try {
      const unitId = await callRpc<string>('fn_save_area_unit', {
        p_code: (code.trim() || label.trim()).toUpperCase().replace(/[^A-Z0-9]+/g, '_'),
        p_label: label.trim(),
        p_fixed: !varies,
      });
      await callRpc('fn_save_area_conversion', {
        p_unit_id: unitId, p_factor: value, p_geography_id: varies ? stateId : null,
        p_note: note.trim() || null, p_confirmed: confirmed,
      });
      await queryClient.invalidateQueries({ queryKey: ['ref', 'unit'] });
      await queryClient.invalidateQueries({ queryKey: ['area_conversions'] });
      toast.success(`Added ${label.trim()}.`);
      onAdded(unitId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The unit could not be added.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="inline-panel">
      <div className="inline-panel-head">
        <strong>New land unit</strong>
        <button aria-label="Cancel" onClick={onCancel} type="button"><X size={16} /></button>
      </div>
      <div className="field-row">
        <Field label="Name" required>
          <input autoFocus onChange={(event) => setLabel(event.target.value)} placeholder="e.g. Guntha" value={label} />
        </Field>
        <Field hint="Optional, derived from the name" label="Code">
          <input onChange={(event) => setCode(event.target.value)} value={code} />
        </Field>
      </div>
      <label className="check-row">
        <input checked={varies} onChange={(event) => setVaries(event.target.checked)} type="checkbox" />
        Its size varies from state to state
      </label>
      <div className="field-row">
        <Field hint="Hectares in one unit" label="1 unit = ha" required>
          <input inputMode="decimal" onChange={(event) => setFactor(event.target.value)} placeholder="0.1337803" value={factor} />
        </Field>
        {varies && (
          <Field label="Applies in state" required>
            <Select onChange={setStateId} options={states.data ?? []}
                    placeholder={states.isLoading ? 'Loading...' : 'Select state'} value={stateId} />
          </Field>
        )}
      </div>
      <Field label="Source">
        <input onChange={(event) => setNote(event.target.value)}
               placeholder="e.g. State land records notification" value={note} />
      </Field>
      <label className="check-row">
        <input checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} type="checkbox" />
        Confirmed against the state's land records (otherwise it is used, but marked unconfirmed)
      </label>
      <div className="inline-panel-foot">
        <button className="primary" disabled={saving} onClick={() => void save()} type="button">
          {saving ? 'Saving...' : 'Add unit'}
        </button>
      </div>
    </div>
  );
}
